// src/features/links/useVisibleColumns.ts
// IR-1238 + IR-1233 r6 — the Join Links table's column selection, in one place.
//
// Two concerns that were previously inline in the route (and therefore
// untested — the IR-1247 lesson: a green unit suite proves nothing about the
// screen when the wiring lives in JSX):
//   1. the analyst's persisted show/hide selection (IR-1238), and
//   2. the r6 auto-reveal of Source Groups when the active search matched only
//      that (default-hidden) column, plus the explicit-hide override that lets
//      them turn it back off for the current query.
import { useMemo, useState } from "react";
import {
  type LinkColumnKey,
  SOURCE_GROUPS_COLUMN,
  effectiveVisibleColumns,
  loadVisibleColumns,
  saveVisibleColumns,
} from "./column-visibility";
import type { SourceGroupRef } from "./types";

interface Options {
  /** Active search query (`filters.q`). */
  q: string | null | undefined;
  /** Currently loaded rows — the auto-reveal only reacts to what is on screen. */
  rows: readonly { source_groups?: readonly SourceGroupRef[] | null }[];
}

export interface VisibleColumnsState {
  /** The analyst's OWN selection — what the Columns picker shows as selected. */
  visibleCols: LinkColumnKey[];
  /** What the table should render (selection + any auto-revealed column). */
  tableCols: LinkColumnKey[];
  /** True when Source Groups is on screen only because of the search. */
  sourceGroupsAutoRevealed: boolean;
  /** Apply a new selection from the picker (persists it). */
  onColumnsChange: (next: string[]) => void;
}

export function useVisibleColumns({ q, rows }: Options): VisibleColumnsState {
  const [visibleCols, setVisibleCols] = useState<LinkColumnKey[]>(() => loadVisibleColumns());
  // The query the analyst was on when they explicitly hid Source Groups. The
  // reveal stands down for THAT query only, so a later search reveals again.
  const [hideOverriddenFor, setHideOverriddenFor] = useState<string | null>(null);

  function onColumnsChange(next: string[]) {
    const cols = next as LinkColumnKey[];
    setVisibleCols(cols);
    saveVisibleColumns(cols);
    setHideOverriddenFor(cols.includes(SOURCE_GROUPS_COLUMN) ? null : (q ?? ""));
  }

  const hideOverridden = hideOverriddenFor != null && hideOverriddenFor === (q ?? "");
  const tableCols = useMemo(
    () => effectiveVisibleColumns(visibleCols, q, rows, hideOverridden),
    [visibleCols, q, rows, hideOverridden],
  );

  return {
    visibleCols,
    tableCols,
    sourceGroupsAutoRevealed: tableCols.length > visibleCols.length,
    onColumnsChange,
  };
}
